<template>
    <div class="random-call-agora">
        <div class="remote-container">
            <video
                v-show="hasRemoteVideoTrack"
                id="random-remote-video"
                ref="remoteVideo"
                playsinline
            ></video>
            <div v-if="!hasRemoteVideoTrack" class="text-center text-white">
                <font-awesome-icon size="4x" icon="fa-duotone fa-camera-slash" />
                <div class="mt-2 small">{{ $t("Connecting...") }}</div>
            </div>
        </div>

        <div class="self-container">
            <video v-show="isCameraOn" id="random-local-video" playsinline ref="localVideo"></video>
            <div
                v-if="!isCameraOn"
                class="bg-dark d-flex align-items-center justify-content-center text-white"
                style="width: 100%; height: 100%"
            >
                <font-awesome-icon icon="fa-duotone fa-camera-slash" />
            </div>
        </div>

        <div class="controls d-flex align-items-center justify-content-center">
            <b-btn
                @click="toggleCamera"
                :variant="isCameraOn ? 'secondary' : 'outline-light'"
                pill
                size="lg"
                class="mx-1"
            >
                <font-awesome-icon
                    :icon="isCameraOn ? 'fa-duotone fa-camera' : 'fa-duotone fa-camera-slash'"
                />
            </b-btn>

            <b-btn
                @click="toggleMicrophone"
                :variant="isMicrophoneOn ? 'secondary' : 'outline-light'"
                pill
                size="lg"
                class="mx-1"
            >
                <font-awesome-icon
                    :icon="isMicrophoneOn ? 'fa-duotone fa-microphone' : 'fa-duotone fa-microphone-slash'"
                />
            </b-btn>

            <b-btn @click="$emit('skip')" variant="warning" pill size="lg" class="mx-1">
                <font-awesome-icon icon="fa-duotone fa-forward" />
                <span class="ml-1">{{ $t("Skip") }}</span>
            </b-btn>

            <b-btn @click="$emit('hangup')" variant="danger" pill size="lg" class="mx-1">
                <font-awesome-icon icon="fa-duotone fa-phone-hangup" />
            </b-btn>
        </div>
    </div>
</template>

<script>
import {
    createCameraVideoTrack,
    createMicrophoneAudioTrack,
} from "agora-rtc-sdk-ng/esm";
import { createClient } from "agora-rtc-sdk-ng/esm";
import { mapState } from "vuex";

import {
    CALL_AUDIO_ENABLED,
    CALL_VIDEO_ENABLED,
} from "@/constant/localStorage";

export default {
    props: {
        channel: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            client: null,
            videoTrack: null,
            audioTrack: null,
            isMicrophoneOn: false,
        };
    },
    computed: {
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
        ...mapState("options", ["agora"]),
        ...mapState("randomCall", ["hasRemoteVideoTrack", "isCameraOn"]),
    },
    async mounted() {
        this.client = await createClient({ mode: "rtc", codec: "vp8" });

        await this.client.join(
            this.agora.appId,
            this.channel,
            null,
            this.onboardingUser.id
        );

        this.client.on("user-published", this.onPublished);
        this.client.on("user-unpublished", this.onUnPublished);

        // default ON for random call (faster session start than private call)
        if (localStorage.getItem(CALL_VIDEO_ENABLED) !== "false") {
            await this.toggleCamera();
        }
        if (localStorage.getItem(CALL_AUDIO_ENABLED) !== "false") {
            await this.toggleMicrophone();
        }
    },
    methods: {
        async toggleCamera() {
            if (!this.videoTrack) {
                this.videoTrack = await createCameraVideoTrack({ facingMode: "user" });
                await this.client.publish(this.videoTrack);
                this.videoTrack.play("random-local-video");
                this.$store.commit("randomCall/setIsCameraOn", true);
                localStorage.setItem(CALL_VIDEO_ENABLED, "true");
                return;
            }

            const next = !this.isCameraOn;
            this.videoTrack.setMuted(!next);
            this.$store.commit("randomCall/setIsCameraOn", next);
            localStorage.setItem(CALL_VIDEO_ENABLED, next ? "true" : "false");
        },
        async toggleMicrophone() {
            if (!this.audioTrack) {
                this.audioTrack = await createMicrophoneAudioTrack();
                await this.client.publish(this.audioTrack);
                this.isMicrophoneOn = true;
                localStorage.setItem(CALL_AUDIO_ENABLED, "true");
                return;
            }

            const next = !this.isMicrophoneOn;
            await this.audioTrack.setMuted(!next);
            this.isMicrophoneOn = next;
            localStorage.setItem(CALL_AUDIO_ENABLED, next ? "true" : "false");
        },
        async onPublished(user, mediaType) {
            await this.client.subscribe(user, mediaType);

            if (mediaType === "video") {
                user.videoTrack?.play("random-remote-video");
                this.$store.commit("randomCall/setHasRemoteVideoTrack", true);
            }

            if (mediaType === "audio") {
                user.audioTrack?.play();
            }
        },
        async onUnPublished(user, mediaType) {
            try {
                await this.client.unsubscribe(user, mediaType);
            } catch (e) {
                // ignore
            }

            if (mediaType === "video") {
                this.$store.commit("randomCall/setHasRemoteVideoTrack", false);
            }
        },
    },
    async beforeDestroy() {
        if (this.client) {
            this.client.off("user-published", this.onPublished);
            this.client.off("user-unpublished", this.onUnPublished);
        }

        if (this.videoTrack) {
            try {
                await this.videoTrack.stop();
                await this.videoTrack.close();
            } catch (e) {
                // ignore
            }
            this.videoTrack = null;
        }

        if (this.audioTrack) {
            try {
                await this.audioTrack.stop();
                await this.audioTrack.close();
            } catch (e) {
                // ignore
            }
            this.audioTrack = null;
        }

        if (this.client) {
            try {
                await this.client.leave();
            } catch (e) {
                // ignore
            }
            this.client = null;
        }
    },
};
</script>

<style scoped>
.random-call-agora {
    position: relative;
    flex: 1;
    background: #000;
    display: flex;
    flex-direction: column;
}

.remote-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #111;
    overflow: hidden;
}

.remote-container video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.self-container {
    position: absolute;
    bottom: 90px;
    right: 12px;
    width: 96px;
    height: 128px;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid #fff;
    background: #000;
    z-index: 2;
}

.self-container video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.controls {
    padding: 12px;
    background: rgba(0, 0, 0, 0.6);
    z-index: 3;
}
</style>
